

import { Badge, Button, Checkbox, Grid, Group, Space, ThemeIcon, Tooltip } from '@mantine/core';
import { HitsPerPage, InstantSearch, Pagination, SearchBox, useHits, InstantSearchProps, Configure, useRefinementList, useSortBy } from 'react-instantsearch';

import TypesenseInstantSearchAdapter from "typesense-instantsearch-adapter";
import { useContext, useState, useMemo, useEffect } from 'react';
import { ConfigContext, MyUserDataContext } from '../../contexts';
import DataTable, { TableColumn } from 'react-data-table-component';

import { useSessionStorage } from '@mantine/hooks';

import { doc, getDoc, Timestamp } from 'firebase/firestore';

import { Service, ServiceStatusCode, ServiceActionRequest, BaseResult, ActionType, ServiceType } from '../../types';
import { TagBadge } from '../../components/tag_badge';
import FilterModal, { FilterSection } from '../../components/FilterModal';
import { FilterBadgeGroup } from '../../components/FilterBadges';
import { serviceLastActionColumn } from '../../components/service_last_action_cell';
import { IconBriefcase, IconBusinessplan, IconCheck, IconDeviceMobile, IconEye, IconMessage, IconMoneybag, IconReportMoney, IconUser } from '@tabler/icons-react';
import { CurrentViewers } from '../../components/current_viewers';
import CustomListActionsModal from "../../components/custom_actions_modal";

import { httpsCallable } from 'firebase/functions';
import { useFirestore, useFunctions } from '../../helpers/firebaseContext';
import { getPortfolioPendingStatusesForClaims } from '../../helpers/serviceStatusFilters';

//typesense

interface InitialState {
    query?: string;
    page?: number;
    sortBy?: string;
}

const TableDisplay = ({ setSelectedRows, selectedRows, renderTags, ActionsComponent, navigateDetails, indexName, clearTrigger, additionalColumns = [], additionalFilterSections = [], additionalFilterBadges, hydratePortfolioRequests = false, showLastActionColumn = false, ...props }: any) => {
    const { items } = useHits();
    const db = useFirestore();
    const configContext = useContext(ConfigContext);
    const [portfolioDetailsByServiceId, setPortfolioDetailsByServiceId] = useState<Record<string, Pick<Service, "portfolioOpeningRequest" | "transferInvoices"> | null>>({});
    const createdDescSortValue = `${indexName}/sort/created:desc`;
    // Typesense sort strings; services without a lastAction yet always go last.
    const sortValues: Record<string, { asc: string; desc: string }> = {
        created: { desc: createdDescSortValue, asc: `${indexName}/sort/created:asc` },
        "lastAction.date": {
            desc: `${indexName}/sort/lastAction.date(missing_values: last):desc`,
            asc: `${indexName}/sort/lastAction.date(missing_values: last):asc`,
        },
    };
    const [openFilterModal, setOpenFilterModal] = useState<boolean>(false);
    const { currentRefinement: currentSortBy, refine: refineSortBy } = useSortBy({
        items: [
            { label: 'Created (desc)', value: sortValues.created.desc },
            { label: 'Created (asc)', value: sortValues.created.asc },
            { label: 'Latest action (desc)', value: sortValues["lastAction.date"].desc },
            { label: 'Latest action (asc)', value: sortValues["lastAction.date"].asc },
        ],
    });
    const knownSortValues = Object.values(sortValues).flatMap((value) => [value.asc, value.desc]);
    const currentSort = Object.entries(sortValues)
        .flatMap(([field, value]) => [{ field, asc: true, value: value.asc }, { field, asc: false, value: value.desc }])
        .find((entry) => entry.value === currentSortBy) ?? { field: "created", asc: false, value: createdDescSortValue };
    const handleSort = (column: TableColumn<Service>, direction: string) => {
        const target = column.sortField ? sortValues[column.sortField] : undefined;
        if (!target) return;
        refineSortBy(direction === "asc" ? target.asc : target.desc);
    };

    const {
        items: serviceStatusItems,
        refine: refineServiceStatus,
    } = useRefinementList({ ...props, attribute: "tags.serviceStatus.code", operator: "or", limit: 9999 });

    const {
        items: serviceTypeItems,
        refine: refineServiceType,
    } = useRefinementList({ ...props, attribute: "serviceType", operator: "or", limit: 9999 });

    // TODO: Dynamic facets implementation
    // Future: Access search results facets and create refinement lists dynamically
    // const { results } = useHits(); 
    // const availableFacets = results?._rawResults?.[0]?.facets || {};
    // Then create FilterSections based on availableFacets keys

    useEffect(() => {
        // Only reset when the stored sort is not one we offer (e.g. a stale value from an older build).
        if (!knownSortValues.includes(currentSortBy)) {
            refineSortBy(createdDescSortValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [createdDescSortValue, currentSortBy, refineSortBy]);

    const handleChange = ({ selectedRows }: any) => {

        // You can set state or dispatch with something like Redux so we can use the retrieved data
        if (setSelectedRows) {
            setSelectedRows(selectedRows);
        }
    };

    const missingPortfolioDetailsRefs = useMemo(() => {
        if (!hydratePortfolioRequests) {
            return [];
        }

        const refsByServiceId = new Map<string, { serviceId: string; customerId: string }>();

        items.forEach((item: any) => {
            const serviceId = item.id;
            const customerId = item.customerObj?.id;

            if (
                item.serviceType === "portfolio" &&
                serviceId &&
                customerId &&
                (!item.portfolioOpeningRequest || item.transferInvoices === undefined) &&
                portfolioDetailsByServiceId[serviceId] === undefined
            ) {
                refsByServiceId.set(serviceId, { serviceId, customerId });
            }
        });

        return Array.from(refsByServiceId.values());
    }, [hydratePortfolioRequests, items, portfolioDetailsByServiceId]);

    useEffect(() => {
        if (!missingPortfolioDetailsRefs.length) {
            return;
        }

        let active = true;

        const loadMissingPortfolioDetails = async () => {
            const results = await Promise.all(missingPortfolioDetailsRefs.map(async ({ serviceId, customerId }) => {
                try {
                    const serviceSnapshot = await getDoc(doc(db, "customers", customerId, "services", serviceId));
                    const service = serviceSnapshot.data() as Service | undefined;
                    return [serviceId, service ? {
                        portfolioOpeningRequest: service.portfolioOpeningRequest,
                        transferInvoices: service.transferInvoices ?? [],
                    } : null] as const;
                } catch (error) {
                    console.error(`Failed to hydrate portfolio details for service ${serviceId}`, error);
                    return [serviceId, null] as const;
                }
            }));

            if (!active) {
                return;
            }

            setPortfolioDetailsByServiceId((current) => {
                const next = { ...current };

                results.forEach(([serviceId, details]) => {
                    next[serviceId] = details;
                });

                return next;
            });
        };

        loadMissingPortfolioDetails();

        return () => {
            active = false;
        };
    }, [db, missingPortfolioDetailsRefs]);


    const columns: TableColumn<Service>[] = [

        {
            // name: "Type",
            id: "type",
            cell: (row) => {



                let icons = [];

                if (row.serviceType === "kfhtrade") icons.push(
                    <ThemeIcon variant="white" color="orange">
                        <IconReportMoney size={16} />


                    </ThemeIcon>
                )
                else if (row.serviceType === "portfolio") icons.push(
                    <ThemeIcon variant="white" color="grape">
                        <IconBriefcase size={16} />
                    </ThemeIcon>
                )

                if (row.tags?.paid) icons.push(
                    <ThemeIcon variant="white" color="yellow"
                    >
                        <IconMoneybag size={16} />
                    </ThemeIcon>
                )
                if (row.fromMobile) icons.push(<ThemeIcon variant="white" color='lime'>
                    <IconDeviceMobile size={16} />
                </ThemeIcon>)

                if (row.documentSent) icons.push(
                    <ThemeIcon variant="white" color="green"
                    >
                        <IconMessage size={16} />
                    </ThemeIcon>
                )
                if (row.serviceType === "kfhtrade" && !!row.customerObj?.kfhtrade_id?.length) icons.push(
                    <ThemeIcon variant="white" color="blue">
                        <IconCheck size={16} />
                    </ThemeIcon>
                )

                return (<>{icons}</>)



            },
            width: "90px",

            // cell(row, rowIndex, column, id) {
            //     return (<div>

            //         <TagBadge tag={{ code: row.type, title: row.type, badgeColor: "green" }} />

            //     </div>)
            // },
            // width: "90px",

            // grow: 1
        },
        {
            name: <Tooltip label="Current Viewers" transitionProps={{ transition: 'scale', duration: 500 }}><IconEye size={16} /></Tooltip>,
            cell(row,) {
                return (
                    <CurrentViewers type="services" id={row.id!} />
                );
            },
            // sortable: true,
            width: "90px",
        },
        {
            name: "Name",
            id: "customerObj.name_en",
            selector: (row) => {

                return row.customerObj?.name_en ?? ""
            },
        },
        {
            name: "Mobile",
            selector: (row) => row.customerObj?.mobile ?? "",

            // sortable: true,
            grow: 1,


        },
        {
            name: "Civil ID",
            id: "customerObj.civilID",
            selector: (row) => row.customerObj?.civilID ?? "",
            grow: 1,

        },

        ...additionalColumns,

        {
            name: "Date",
            id: "created",
            sortable: true,
            sortField: "created",
            selector: (row) => {
                let created = row.created;

                // console.log(created);
                // console.log("Created: ", row.created);
                if (!(created instanceof Timestamp)) {
                    created = new Timestamp(created as any, 0);
                    row.created = created;
                }
                return `${created.toDate().toLocaleString("en-GB", { hour12: true }) ?? row.created}`
            },

        },
        ...(showLastActionColumn ? [serviceLastActionColumn] : []),
        {
            name: "Segment",
            id: "customerObj.segment",
            cell: (row: Service) => {

                // Find the segment config for the current row
                const config = configContext.segments?.segmentList?.find(s => s.name === row?.customerObj?.segment);

                if (config) {
                    return <Badge color={config.color} size="sm">{row?.customerObj?.segment}</Badge>;
                }

                return <span></span>;
            },
            grow: 1,

        },


        {
            name: "Tags",
            // wrap: true,
            // width: "150px",
            cell: (row: Service) => {
                if (!row.tags) return;

                // return <TagBadge tag={row.tags["serviceStatus"]} />
                return <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    <TagBadge m={3} tag={row.tags["serviceStatus"]} key="serviceStatus" />
                    {row.tags["riskStatus"] && <TagBadge m={3} tag={row.tags["riskStatus"]} key="riskStatus" />}
                    {row.tags["kycStatus"] && <TagBadge m={3} tag={row.tags["kycStatus"]} key="kycStatus" />}
                </div>

            }
        },

        {
            name: "",
            selector: (row: any) => <>

                {ActionsComponent && <ActionsComponent serviceObj={row} />}
            </> as any,

            grow: 0,

        }


    ];



    return <Grid justify='center' align='center'>
        <Grid.Col span={12}>
            <Group my="sm">
                <SearchBox
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        width: "90%",
                    }}
                    autoFocus
                    placeholder='Search...'
                />
                <Button onClick={() => setOpenFilterModal(true)}>
                    Filter
                </Button>
            </Group>

            <FilterBadgeGroup
                items={serviceStatusItems}
                color="blue"
                refine={refineServiceStatus}
            />
            <FilterBadgeGroup
                items={serviceTypeItems}
                color="yellow"
                refine={refineServiceType}
            />
            {additionalFilterBadges}
        </Grid.Col>

        <Grid.Col span={12} mt={0}>
            <DataTable
                columns={columns}
                selectableRows
                onSelectedRowsChange={handleChange}
                key={clearTrigger}
                sortServer
                onSort={handleSort}
                defaultSortFieldId={currentSort.field}
                defaultSortAsc={currentSort.asc}
                data={items.map((hitObj: any) => {

                    return {
                        ...hitObj,
                        portfolioOpeningRequest: portfolioDetailsByServiceId[hitObj.id]?.portfolioOpeningRequest ?? hitObj.portfolioOpeningRequest,
                        transferInvoices: portfolioDetailsByServiceId[hitObj.id]?.transferInvoices ?? hitObj.transferInvoices,
                        customerID: hitObj?.customerObj?.civilID,
                    }
                })}
            />
        </Grid.Col>

        <FilterModal
            opened={openFilterModal}
            onClose={() => setOpenFilterModal(false)}
            sections={[
                {
                    id: 'serviceStatus',
                    title: 'Filter by Service Status',
                    items: serviceStatusItems,
                    refine: refineServiceStatus,
                },
                {
                    id: 'serviceType',
                    title: 'Filter by Service Type',
                    items: serviceTypeItems,
                    refine: refineServiceType,
                },
                ...additionalFilterSections,
                // TODO: Add more sections dynamically based on facets
                // Future enhancement: Parse facets from search results and create sections automatically
            ] as FilterSection[]}
        />
    </Grid>
}

interface ServicesInstantSearchProps {
    indexName: string,
    setSelectedRows?: any
    renderTags?: boolean
    ActionsComponent?: any
    navigateDetails?: any
    selectable?: boolean
    selectedRows?: any[]
    customFilters?: string
    topElements?: JSX.Element
    additionalChildren?: JSX.Element
    additionalColumns?: TableColumn<Service>[]
    additionalFilterSections?: FilterSection[]
    additionalFilterBadges?: JSX.Element
    filterClauses?: string[]
    /** Adds a "Latest action" column (date + customer/team) fed by the Typesense lastAction field. */
    showLastActionColumn?: boolean
    hydratePortfolioRequests?: boolean
    serviceType?: ServiceType
}
const ServicesInstantSearch = ({ indexName, selectable, setSelectedRows, selectedRows = [], navigateDetails, renderTags, ActionsComponent, topElements, additionalChildren, additionalColumns, additionalFilterSections, additionalFilterBadges, filterClauses, hydratePortfolioRequests, showLastActionColumn, customFilters, serviceType }: ServicesInstantSearchProps) => {


    const serviceTypeValue = serviceType || "";
    const [serviceStatusFilter, setServiceStatusFilter] = useState<string | null>("")
    const [serviceStatusUpdateOpen, setServiceStatusUpdateOpen] = useState(false);
    const userData = useContext(MyUserDataContext);
    let customClaims = userData.firebaseTokenResult?.claims.customClaims as any;

    const functions = useFunctions();
    const serviceAction = httpsCallable<ServiceActionRequest, BaseResult>(functions, 'serviceAction');
    const [tableClearTrigger, setTableClearTrigger] = useState(0);




    const handleRowsToShow = () => {

        let serviceStatuses: string[] = []

        if (serviceType === "portfolio") {
            serviceStatuses = getPortfolioPendingStatusesForClaims(customClaims);
        } else {
            if (customClaims.cr) {
                serviceStatuses.push(...[ServiceStatusCode.authorized_signatory_approval, ServiceStatusCode.review, ServiceStatusCode.approval, ServiceStatusCode.waiting_for_kyc, ServiceStatusCode.waiting_for_execution, ServiceStatusCode.need_iban])
            }
            if (customClaims.oper) {
                serviceStatuses.push(...[ServiceStatusCode.waiting_for_operations_approval])
            }
        }


        if (serviceStatuses.length) {

            setServiceStatusFilter(`tags.serviceStatus.code:[${serviceStatuses.join()}] `)

        } else {

            setServiceStatusFilter(null);

        }



    }

    const typesenseSearchClient = useMemo(() => new TypesenseInstantSearchAdapter({
        server: {
            apiKey: userData.servicesSearchKey!, // Be sure to use a Search API Key
            nodes: [
                {
                    host: import.meta.env.VITE_TYPESENSE_SEARCH_HOST!,
                    port: 443,
                    protocol: 'https'
                },
            ],
        },

        additionalSearchParameters: {

            query_by: "customerObj,tags"
        }

    }).searchClient, [userData.servicesSearchKey]);

    if (!indexName) {
        indexName = import.meta.env.VITE_TYPESENSE_SERVICES_COLLECTION!;
    }

    // Sort values look like `${indexName}/sort/<typesense sort_by>`; anything else falls back to newest first.
    const normalizeSortBy = (value?: string) =>
        value && value.startsWith(`${indexName}/sort/`) ? value : `${indexName}/sort/created:desc`;

    const searchStateScope = `${userData.user?.uid ?? 'anon'}_${(userData.servicesSearchKey ?? '').slice(0, 12)}`;

    //save to local session
    const [initialUIStateForIndex, setInitialUIStateForIndex] = useSessionStorage<InitialState>({
        key: `${serviceTypeValue}_initialUIStateFor_${indexName}_${searchStateScope}`,
        getInitialValueInEffect: false,
    });


    const [initialUIStateForCheckbox, setInitialUIStateForCheckbox] = useSessionStorage<boolean>({
        key: `${serviceTypeValue}_initialUIStateForCheckbox_${searchStateScope}`,
        getInitialValueInEffect: false,
    });

    useEffect(() => {
        if (!initialUIStateForCheckbox) {

            handleRowsToShow();
        }
        else {
            setServiceStatusFilter(null)
        }
    }, [initialUIStateForCheckbox])


    const onStateChange: InstantSearchProps['onStateChange'] = ({
        uiState,
        setUiState,
    }) => {
        setUiState(uiState);
        setInitialUIStateForIndex({
            ...(uiState[indexName] ?? {}),
            sortBy: normalizeSortBy(uiState[indexName]?.sortBy),
        });
    };

    // console.log("session for ", indexName, initialUIStateForIndex);

    // if (!initialUIStateForIndex)  {
    //     return <div></div>;
    // }


    const customerModals = useMemo(() => <>
        <CustomListActionsModal<Service>
            rows={selectedRows}
            handleClose={() => {
                setSelectedRows([]);
                // Force table to clear by changing a key
                setTableClearTrigger(prev => prev + 1);
            }}
            opened={serviceStatusUpdateOpen}
            setOpened={setServiceStatusUpdateOpen}
            availableActions={[
                {
                    value: "authorized_signatory_approve",
                    label: "Authorized Signatory Approve"
                },

                {
                    value: "execution",
                    label: "Execute"
                },
                {
                    value: "activation",
                    label: "Activate"
                },

            ]
            }
            handleSubmit={
                async (serviceData: Service, action: string, token?: string) => {
                    if (!serviceData.id || !serviceData.customerObj?.id || !token) {
                        throw new Error("Missing required data for service action: serviceID, customerID, or token is undefined.");
                    }
                    await serviceAction({
                        action: action as ActionType,
                        serviceID: serviceData.id!,
                        customerID: serviceData.customerObj?.id!,
                        token: token!,
                    });
                }


            }
            renderErrors={(failedItem: any) =>
                <>
                    {failedItem.name_en}

                    <Space h="xl" />

                </>
            }
            getItemsData={
                (action: string, rows: Service[]) => {
                    let data: Service[] = []
                    if (action === "authorized_signatory_approve") {
                        data = rows.filter((service: Service) => {
                            return service?.tags?.serviceStatus.code === "authorized_signatory_approval"
                        })
                    }

                    if (action === "add_subaccount_id") {
                        data = rows.filter((service: Service) => {
                            return service?.tags?.serviceStatus.code === "waiting_for_sub_account_id"
                        })
                    }

                    if (action === "execution") {
                        data = rows.filter((service: Service) => {
                            return service?.tags?.serviceStatus.code === "waiting_for_execution"
                        })
                    }

                    if (action === "activation") {
                        data = rows.filter((service: Service) => {
                            return service?.tags?.serviceStatus.code === "waiting_for_activation"
                        })
                    }

                    return data
                }
            }
        />
    </>,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedRows, serviceStatusUpdateOpen]);
    const normalizedCustomFilters = customFilters
        ?.trim()
        // Typesense expects logical operators as &&/||; strip any leading AND/&&.
        .replace(/^(AND|&&)\s+/i, "");
    const normalizedFilterClauses = (filterClauses ?? [])
        .map((filterClause) => filterClause.trim().replace(/^(AND|&&)\s+/i, ""))
        .filter(Boolean);
    const initialIndexUiState = useMemo(() => ({
        ...(initialUIStateForIndex ?? {}),
        sortBy: normalizeSortBy(initialUIStateForIndex?.sortBy),
    }), [indexName, initialUIStateForIndex]);
    return (
        <>
            {customerModals}
            <InstantSearch

                searchClient={typesenseSearchClient}
                indexName={indexName}
                initialUiState={{
                    [indexName]: initialIndexUiState
                }}
                onStateChange={onStateChange}


            >




                <Configure
                    // Typesense uses &&/|| for logical operators.
                    filters={[serviceStatusFilter, normalizedCustomFilters, ...normalizedFilterClauses].filter(Boolean).join(" && ")}
                />

                <Group justify="space-between" >

                    {topElements}

                    <Checkbox
                        label={`Show all ${serviceType} services`}
                        // defaultChecked={!customClaims.cr || !customClaims.oper}
                        checked={initialUIStateForCheckbox || false}
                        onChange={(event) => {

                            setInitialUIStateForCheckbox(event.currentTarget.checked);

                        }

                        }
                    />


                    <Button
                        color="lime"
                        onClick={() => setServiceStatusUpdateOpen(true)}
                        disabled={selectedRows.length === 0}
                    >
                        {`Change Service status for ${selectedRows.length} service(s)`}
                    </Button>


                    {additionalChildren}

                </Group>

                <TableDisplay
                    selectedRows={selectedRows}
                    setSelectedRows={selectable ? setSelectedRows : undefined}
                    navigateDetails={navigateDetails}
                    ActionsComponent={ActionsComponent}
                    renderTags={renderTags}
                    indexName={indexName}
                    clearTrigger={tableClearTrigger}
                    additionalColumns={additionalColumns}
                    additionalFilterSections={additionalFilterSections}
                    additionalFilterBadges={additionalFilterBadges}
                    hydratePortfolioRequests={hydratePortfolioRequests}
                    showLastActionColumn={showLastActionColumn}
                />

                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: 12,
                        width: "100%",
                    }}
                >

                    <HitsPerPage
                        items={[
                            { label: '250', value: 250, },
                            { label: '100', value: 100 },
                            { label: '50', value: 50, default: true },
                        ]}
                    />
                    <Pagination
                        style={{
                            display: "flex",
                            justifyContent: "flex-end",
                        }}
                    />
                </div>

            </InstantSearch >
        </>


    )
}

export default ServicesInstantSearch
